import React, { useEffect, Suspense } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import useStyles from 'isomorphic-style-loader/useStyles';
import classNames from 'classnames';
import Rating from '@material-ui/lab/Rating';
import NoSsr from '@material-ui/core/NoSsr';
import Alert from '@material-ui/lab/Alert';

// app: services
import assets from 'services/assets';

// app: assets
import CssInlinePaywall from './InlinePaywall.scss';

// Content screens - lazy load
const AccessCheckErrorPaywallContent = React.lazy(() =>
  import(/* webpackChunkName: 'access-check-error-paywall' */ './AccessCheckErrorPaywallContent'),
);
const AccountOnHoldPaywallContent = React.lazy(() =>
  import(/* webpackChunkName: 'account-on-hold-paywall' */ './AccountOnHoldPaywallContent'),
);
const AlacartePaywallContent = React.lazy(() =>
  import(/* webpackChunkName: 'alacarte-paywall' */ './AlacartePaywallContent'),
);
const UpgradePaywallContent = React.lazy(() =>
  import(/* webpackChunkName: 'upgrade-paywall' */ './UpgradePaywallContent'),
);
const JoinPaywallContent = React.lazy(() =>
  import(/* webpackChunkName: 'join-paywall' */ './JoinPaywallContent'),
);
const VoucherPendingPaywallContent = React.lazy(() =>
  import(/* webpackChunkName: 'voucher-pending-paywall' */ './VoucherPendingPaywallContent'),
);

function InlinePaywall(props) {
  const {
    alacarte,
    children,
    className,
    error,
    fullscreen,
    heading,
    visible,
    loading,
    refreshAccessPolicy,
    sticky,
    usingAdminAccess,
    ...otherProps
  } = props;
  useStyles(CssInlinePaywall);
  const tableOfContentsOpen = useSelector(state => state.tableOfContents.isOpen);
  const loggedIn = useSelector(state => state.user.logged_in);
  const voucherClaimPending = useSelector(state => state.paywall.voucherClaimPending);
  const requestedCoupon = useSelector(state => state.paywall.requestedCoupon);
  const urlHash = useSelector(state => state.router.hash);
  const isCorporate = useSelector(state => state.user.geolocation.corporate);
  const accountOnHold = useSelector(
    state =>
      state.user.subscription
        ? (state.user.subscription.status === 'past_due' && state.user.subscription.hard_failure) ||
          state.user.subscription.status === 'paused'
        : false,
  );

  // If the URL has a #hash the content might not be in the DOM until the inline paywall has hidden.
  // Therefore we should check for URL hashes that need resolving once the paywall hides.
  useEffect(
    () => {
      if (!visible && urlHash) {
        const targetHash = urlHash.substr(1);
        if (targetHash) {
          const target = document.getElementById(targetHash);
          if (target) {
            const rect = target.getBoundingClientRect();
            window.scrollTo({
              top: rect.top - 60,
              left: 0,
              behavior: 'smooth',
            });
          }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visible],
  );

  if (visible && !isCorporate) {
    let content;
    const loadingContent = <div className="inline-paywall__loading-content" />;
    if (loading || error === 'TOKEN_EXPIRED') {
      // Access check pending. Perhaps add a loading indicator or skeleton here.
      // SSR will send this.
      content = loadingContent;
    } else if (error) {
      console.warn('Error passed to inline paywall', error);
      content = (
        <AccessCheckErrorPaywallContent
          error={error}
          refreshAccessPolicy={refreshAccessPolicy}
          {...otherProps}
        />
      );
    } else if (alacarte) {
      content = <AlacartePaywallContent {...otherProps} />;
    } else if (accountOnHold) {
      content = (
        <AccountOnHoldPaywallContent refreshAccessPolicy={refreshAccessPolicy} {...otherProps} />
      );
    } else if (!loggedIn && voucherClaimPending) {
      content = <VoucherPendingPaywallContent {...otherProps} />;
    } else if (loggedIn) {
      content = <UpgradePaywallContent heading={heading} {...otherProps} />;
    } else {
      content = <JoinPaywallContent heading={heading} {...otherProps} />;
    }
    if (content) {
      const backgroundStyle =
        loading || error
          ? {}
          : {
              backgroundImage: `-webkit-image-set(
              url(${assets.backdropUrl('bookgrid.jpg', {
                resize: 'fit-in',
                width: 1024,
                height: 768,
              })}) 1x,
              url(${assets.backdropUrl('bookgrid.jpg', {
                resize: 'fit-in',
                width: 2048,
                height: 1536,
              })}) 2x
            )`,
            };
      const joinUrl = new URL('https://join.ckbk.com/ckbk/');
      if (requestedCoupon) {
        joinUrl.search = `?code=${requestedCoupon}`;
      }
      return (
        <React.Fragment>
          {children ? (
            <div className="inline-paywall__obscured-content">
              {/* Only render the first child as the rest will be hidden. This avoids wasted GQL queries for stuff thats invisible */}
              {Array.isArray(children) && children.length ? children[0] : children}
            </div>
          ) : null}
          <div
            className={classNames(className, 'inline-paywall', {
              'inline-paywall--sticky': sticky,
              'inline-paywall--loading': loading,
              'inline-paywall--with-toc': tableOfContentsOpen,
              'inline-paywall--with-obscured-content': children,
              'inline-paywall--fullscreen': fullscreen,
            })}
            data-cy="inline-paywall"
          >
            {children ? <div className="inline-paywall__fog" /> : null}
            <div className="inline-paywall__wrapper" style={backgroundStyle}>
              <div className="inline-paywall__content">
                <NoSsr fallback={loadingContent}>
                  <Suspense fallback={loadingContent}>{content}</Suspense>
                </NoSsr>
              </div>
              {loading || error ? null : (
                <div className="inline-paywall__stripe">
                  <div className="inline-paywall__stripe-content">
                    <a href={joinUrl.toString()}>
                      <Rating value={5} readOnly />
                      &apos;Brilliant app for foodies&apos;
                    </a>
                  </div>
                  <div className="inline-paywall__stripe-content">
                    <a href={joinUrl.toString()}>
                      <Rating value={5} readOnly />
                      &apos;Spotify of cookbooks&apos;
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </React.Fragment>
      );
    }
  }

  const adminAccessWarning = usingAdminAccess ? (
    <Alert className="margin-bottom-10" severity="info">
      You do not own this à la carte book, but you have special access as a ckbk tester
    </Alert>
  ) : null;

  // The 'paywalled-content' classname is used to mark the enclosed content as paywalled for Google.
  // Don't remove unless you know what you are doing.
  return children ? (
    <div className="paywalled-content">
      {adminAccessWarning}
      {children}
    </div>
  ) : null;
}

InlinePaywall.propTypes = {
  alacarte: PropTypes.bool,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  className: PropTypes.string,
  error: PropTypes.string,
  fullscreen: PropTypes.bool,
  heading: PropTypes.string,
  loading: PropTypes.bool,
  refreshAccessPolicy: PropTypes.func,
  sticky: PropTypes.bool,
  usingAdminAccess: PropTypes.bool,
  visible: PropTypes.bool,
};
InlinePaywall.defaultProps = {
  alacarte: false,
  children: null,
  className: null,
  error: null,
  fullscreen: false,
  heading: undefined,
  loading: false,
  refreshAccessPolicy: PropTypes.func,
  sticky: false,
  usingAdminAccess: false,
  visible: true,
};

export default InlinePaywall;
